import logger from '../utils/logger';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('USD');
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [countryCode, setCountryCode] = useState('US');
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(true);

  const fallbackToBrowserLocale = useCallback(() => {
    setCurrency('USD');
    setCurrencySymbol('$');
    setCountryCode('US');
  }, []);

  const initializeCurrency = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check for URL override first
      const urlParams = new URLSearchParams(window.location.search);
      const currencyOverride = urlParams.get('currency');
      
      if (currencyOverride && currencyOverride === 'USD') {
        setCurrency(currencyOverride);
        setCurrencySymbol('$');
        setCountryCode('US');
        
        // Persist to localStorage
        localStorage.setItem('cc_currency', currencyOverride);
        return;
      }

      // Check localStorage for saved preference
      const savedCurrency = localStorage.getItem('cc_currency');
      if (savedCurrency && savedCurrency === 'USD') {
        setCurrency(savedCurrency);
        setCurrencySymbol('$');
        setCountryCode('US');
        return;
      }

      // Use browser locale only (geo API disabled to fix mixed content)
      fallbackToBrowserLocale();
      
    } catch (error) {
  logger.error('Error initializing currency:', error);
      // Default to USD
      setCurrency('USD');
      setCurrencySymbol('$');
      setCountryCode('US');
    } finally {
      setLoading(false);
    }
  }, [fallbackToBrowserLocale]);

  useEffect(() => {
    initializeCurrency();
  }, [initializeCurrency]);



  const updateCurrency = (newCurrency) => {
    if (newCurrency === 'USD') {
      setCurrency(newCurrency);
      setCurrencySymbol('$');
      setCountryCode('US');
      
      // Persist to localStorage
      localStorage.setItem('cc_currency', newCurrency);
    }
  };

  const toggleYearly = () => {
    setYearly(!yearly);
  };

  const value = {
    currency,
    currencySymbol,
    countryCode,
    yearly,
    loading,
    updateCurrency,
    toggleYearly,
    setYearly
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
